import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { councilApiClient } from '../../services/councilApiClient';

export interface FoldBackDialogProps {
  sessionId: string;
  /** The Camp's spine_root_message_id to fold back. */
  campId: string;
  /** Optional display name of the companion (for the prompt copy). */
  campLabel?: string;
  onClose: () => void;
  onProposed?: (proposalId: string) => void;
}

/**
 * FoldBackDialog (C2.2) — the operator fold-back affordance. Confirms folding a
 * resolved Camp back into the main trail, with an optional steer note, and
 * enqueues a GATED propose_merge (POST council/propose-merge). The operator
 * approves it in the proposal queue before the host synthesis runs. Prop-driven
 * (the camp is chosen at the trigger) so it stays decoupled + testable.
 */
export const FoldBackDialog: React.FC<FoldBackDialogProps> = ({
  sessionId, campId, campLabel, onClose, onProposed,
}) => {
  const { darkMode } = useTheme();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await councilApiClient.proposeMerge(sessionId, {
        camp_id: campId,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onProposed?.(res.id);
      onClose();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to propose the fold-back');
      setBusy(false);
    }
  };

  const fieldCls = `w-full mb-3 rounded border px-2 py-1.5 text-sm ${
    darkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div
      data-testid="fold-back-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40 p-4"
    >
      <div className={`w-full max-w-md rounded-lg p-5 shadow-xl ${
        darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
      }`}>
        <h2 className="text-lg font-semibold mb-1">Fold {campLabel ?? 'this companion'} back into the trail</h2>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          The host weaves their findings into one synthesis turn on the main trail. You'll approve
          it before it runs.
        </p>

        <label className="block text-xs uppercase tracking-wide mb-1 text-gray-500 dark:text-gray-400">
          Steer (optional)
        </label>
        <textarea
          data-testid="fold-back-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="e.g. Lead with the auth-gap finding."
          className={fieldCls}
        />

        {error && (
          <p data-testid="fold-back-error" className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="fold-back-cancel"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600
                       text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="fold-back-submit"
            onClick={submit}
            disabled={busy}
            className={`text-sm px-3 py-1.5 rounded font-medium text-white
                        bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500
                        ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {busy ? 'Proposing…' : 'Propose fold-back'}
          </button>
        </div>
      </div>
    </div>
  );
};
